import { memo, useCallback, type ReactNode } from 'react';
import { Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { getPlanPageThemeColors } from 'redux/ThemeSlice';

import {
  DriverPanelCodeBranchIcon,
  DriverPanelTableListIcon,
} from '../icons/DriverPanelFigmaIcons';
import { PLAN_BUILDER_MODE_BG } from '../planPageRevampUi';

export type DriverPanelViewMode = 'property' | 'table';

/** Figma 75913:6243 */
const TOGGLE_WIDTH = '138px';
const TOGGLE_HEIGHT = '32px';
const ICON_SIZE = 16;

interface Properties {
  viewMode: DriverPanelViewMode;
  onChange: (mode: DriverPanelViewMode) => void;
}

interface SegmentProperties {
  isActive: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}

const ToggleSegment = memo(({ isActive, label, onClick, children }: SegmentProperties) => (
  <Flex
    as='button'
    type='button'
    aria-label={label}
    aria-pressed={isActive}
    flex='1 1 0'
    minW={0}
    h={TOGGLE_HEIGHT}
    p={0}
    m={0}
    border='none'
    alignItems='center'
    justifyContent='center'
    borderRadius='12px'
    bg={isActive ? PLAN_BUILDER_MODE_BG : 'transparent'}
    cursor='pointer'
    transition='background 0.15s ease'
    _hover={{ bg: isActive ? PLAN_BUILDER_MODE_BG : 'rgba(0, 0, 0, 0.04)' }}
    _focus={{ outline: 'none' }}
    _focusVisible={{ boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.45)' }}
    onClick={onClick}
  >
    {children}
  </Flex>
));

ToggleSegment.displayName = 'ToggleSegment';

const DriverPanelViewToggle = ({ viewMode, onChange }: Properties) => {
  const themeColors = useSelector(getPlanPageThemeColors);
  const isProperty = viewMode === 'property';

  const onPropertyClick = useCallback(() => onChange('property'), [onChange]);
  const onTableClick = useCallback(() => onChange('table'), [onChange]);

  const activeIconColor = themeColors.white;
  const inactiveIconColor = themeColors.textDark;

  return (
    <Flex
      role='group'
      aria-label='Driver view'
      alignItems='center'
      w={TOGGLE_WIDTH}
      minW={TOGGLE_WIDTH}
      maxW={TOGGLE_WIDTH}
      h={TOGGLE_HEIGHT}
      boxSizing='border-box'
      bg={themeColors.surface}
      border='1px solid'
      borderColor={themeColors.border}
      borderRadius='12px'
      gap='1px'
      flexShrink={0}
      overflow='hidden'
    >
      <ToggleSegment isActive={isProperty} label='Org chart view' onClick={onPropertyClick}>
        <DriverPanelCodeBranchIcon
          width={ICON_SIZE}
          height={ICON_SIZE}
          color={isProperty ? activeIconColor : inactiveIconColor}
        />
      </ToggleSegment>
      <ToggleSegment isActive={!isProperty} label='Table view' onClick={onTableClick}>
        <DriverPanelTableListIcon
          width={ICON_SIZE}
          height={ICON_SIZE}
          color={!isProperty ? activeIconColor : inactiveIconColor}
        />
      </ToggleSegment>
    </Flex>
  );
};

export default memo(DriverPanelViewToggle);
